import { useState, useEffect } from "react";
import supabase from "../lib/supabase";
import { imgUrls } from "../lib/imageUrl";

// Transforms a DB bouquet row into the shape the frontend components expect.
// size_prices is kept as-is from the DB (e.g. { medium: 80, large: 120 })
// so the modal can display exact DB names and prices.
function transform(b) {
  const occasions = (b.bouquet_occasion ?? [])
    .map((bo) => bo.occasion?.name)
    .filter(Boolean);

  const category =
    occasions[0]?.toLowerCase() ??
    b.category?.category_name?.toLowerCase() ??
    "other";

  return {
    id: b.id,
    name: b.bouquet_name,
    price: Number(b.price),
    category,
    occasions,
    ordered: b.total_times_sold ?? 0,
    images:      imgUrls(b.image_urls?.length ? b.image_urls : [b.image_url].filter(Boolean), { width: 600,  height: 750,  quality: 85, resize: "contain" }),
    images_full: (b.image_urls?.length ? b.image_urls : [b.image_url].filter(Boolean)),
    description: b.description ?? "",
    is_featured: b.is_featured,
    flowers: b.flowers ?? [],
    size_prices: b.size_prices ?? null,
    height_cm: b.height_cm ?? null,
    width_cm: b.width_cm ?? null,
    rating: b.avg_rating ?? null,
    variants: b.variants ?? [],
    bouquet_type: b.bouquet_type ?? [],
  };
}

export function useBouquets() {
  const [bouquets, setBouquets] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function fetch() {
      setLoading(true);
      const [{ data, error }, { data: variantsData }] = await Promise.all([
        supabase
          .from("bouquet")
          .select(`*, category:category_id(category_name), review(rating), bouquet_occasion(occasion:occasion_id(name))`)
          .eq("is_available", true)
          .order("total_times_sold", { ascending: false }),
        supabase
          .from("bouquet_variants")
          .select("*")
          .eq("active", true)
          .order("sort_order"),
      ]);

      if (cancelled) return;
      if (error) {
        setError(error);
      } else {
        // Group variants by bouquet_id
        const variantMap = {};
        for (const v of variantsData ?? []) {
          if (!variantMap[v.bouquet_id]) variantMap[v.bouquet_id] = [];
          variantMap[v.bouquet_id].push(v);
        }
        const withRating = (data ?? []).map((b) => {
          const reviews = b.review ?? [];
          const avg = reviews.length > 0
            ? (reviews.reduce((s, r) => s + r.rating, 0) / reviews.length).toFixed(1)
            : null;
          const variants = (variantMap[b.id] ?? []).map((v) => ({
            ...v,
            image_urls:      imgUrls(v.image_urls, { width: 600,  height: 750,  quality: 85, resize: "contain" }),
            image_urls_full: v.image_urls ?? [],
          }));
          return transform({ ...b, avg_rating: avg, variants });
        });
        // Shuffle so order varies on every page load
        for (let i = withRating.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [withRating[i], withRating[j]] = [withRating[j], withRating[i]];
        }
        setBouquets(withRating);
      }
      setLoading(false);
    }

    fetch();
    return () => { cancelled = true; };
  }, []);

  return { bouquets, loading, error };
}
